import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MoodCard = ({ mood, isSelected, onSelect, onPreview }) => {
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy':
        return 'text-accent border-accent shadow-glow-green-sm';
      case 'Medium':
        return 'text-warning border-warning';
      case 'Hard':
        return 'text-error border-error';
      case 'Extreme':
        return 'text-secondary border-secondary shadow-glow-magenta-sm';
      default:
        return 'text-primary border-primary';
    }
  };

  const getUnlockRequirementColor = (type) => {
    switch (type) {
      case 'gems':
        return 'text-accent';
      case 'wins':
        return 'text-primary';
      case 'streak':
        return 'text-warning';
      default:
        return 'text-foreground';
    }
  };

  return (
    <div
      className={`
        relative bg-card border-2 rounded-sm overflow-hidden
        transition-all duration-base
        ${isSelected 
          ? 'border-primary shadow-glow-cyan-lg scale-105' 
          : mood?.isLocked 
            ? 'border-muted opacity-60' :'border-primary/50 hover:border-primary hover:shadow-glow-cyan-md hover:scale-102'
        }
        ${mood?.isLocked ? 'cursor-not-allowed' : 'cursor-pointer'}
      `}
      onClick={() => !mood?.isLocked && onSelect(mood?.id)}
    >
      {mood?.isNew && !mood?.isLocked && (
        <div className="absolute top-2 right-2 z-10 px-2 py-1 bg-accent border border-accent rounded-sm shadow-glow-green-sm">
          <span className="text-xs font-caption font-bold text-accent-foreground">NEW</span>
        </div>
      )}
      {mood?.isLocked && (
        <div className="absolute inset-0 z-10 bg-background/80 backdrop-blur-sm flex items-center justify-center">
          <div className="text-center p-4 md:p-6">
            <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 bg-muted/50 rounded-sm flex items-center justify-center border border-muted">
              <Icon name="Lock" size={32} color="var(--color-muted-foreground)" />
            </div>
            <h4 className="font-heading font-semibold text-sm md:text-base text-foreground mb-2 md:mb-3">Locked</h4>
            <div className="space-y-1 md:space-y-2">
              {mood?.unlockRequirements?.map((req, index) => (
                <div key={index} className="flex items-center justify-center gap-2 text-xs md:text-sm">
                  <Icon name={req?.icon} size={16} color={`var(--color-${req?.type === 'gems' ? 'accent' : req?.type === 'wins' ? 'primary' : 'warning'})`} />
                  <span className={`font-data ${getUnlockRequirementColor(req?.type)}`}>
                    {req?.current}/{req?.required} {req?.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="p-4 md:p-6">
        <div className="flex items-start gap-3 md:gap-4 mb-3 md:mb-4">
          <div className="w-16 h-16 md:w-20 md:h-20 flex-shrink-0 bg-background border-2 border-primary/30 rounded-sm overflow-hidden">
            <Image
              src={mood?.avatar}
              alt={mood?.avatarAlt}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-bold text-base md:text-lg text-primary mb-1 truncate">
              {mood?.name}
            </h3>
            <div className={`inline-flex items-center gap-1 px-2 py-1 border rounded-sm ${getDifficultyColor(mood?.difficulty)}`}>
              <Icon name="Zap" size={14} />
              <span className="text-xs font-caption font-semibold">{mood?.difficulty}</span>
            </div>
          </div>
        </div>

        <p className="text-xs md:text-sm text-muted-foreground font-caption mb-3 md:mb-4 line-clamp-2">
          {mood?.description}
        </p>

        <div className="space-y-2 md:space-y-3 mb-3 md:mb-4">
          <div className="flex items-start gap-2">
            <Icon name="Brain" size={16} color="var(--color-secondary)" className="flex-shrink-0 mt-0.5" />
            <p className="text-xs md:text-sm text-foreground font-caption line-clamp-2">
              {mood?.personality}
            </p>
          </div>
          <div className="flex items-start gap-2">
            <Icon name="MessageSquare" size={16} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
            <p className="text-xs md:text-sm text-accent font-data italic line-clamp-2">
              "{mood?.sampleDialogue}"
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!mood?.isLocked && (
            <>
              <Button
                variant={isSelected ? "default" : "outline"}
                size="sm"
                iconName={isSelected ? "Check" : "Play"}
                iconPosition="left"
                onClick={(e) => {
                  e?.stopPropagation();
                  onSelect(mood?.id);
                }}
                fullWidth
              >
                {isSelected ? 'Selected' : 'Select'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="Eye"
                onClick={(e) => {
                  e?.stopPropagation();
                  onPreview(mood);
                }}
                className="flex-shrink-0"
              />
            </>
          )}
        </div>
      </div>
      {isSelected && (
        <div className="absolute inset-0 pointer-events-none border-2 border-primary rounded-sm neon-pulse" />
      )}
    </div>
  );
};

export default MoodCard;